import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { SessionStorageService } from '@services/storage.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { CuttingService, IResponseSetup, ISetup } from '@shared/services/cutting.service';
import { SetupTransferService } from '@shared/services/export-import-services/setup';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { catchError, finalize, map, Subject, takeUntil, tap, throwError } from 'rxjs';
import { CreateUpdateSetupDialogComponent } from './create-update-setup/create-update-setup';

@Component({
	selector: 'app-setups',
	templateUrl: './setups.component.html',
	styleUrls: ['./setups.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class SetupsComponent implements OnInit {
	public registerName: string = '';
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public tableSource = new MatTableDataSource<ISetup>();
	public setupsFilter: string = '';
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public selected: object[] = [];
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.cutting.name', dataKey: 'name' },
		{ name: 'masterdata.cutting.description', dataKey: 'description' },
		{
			name: 'masterdata.cutting.dimensions.length.minimum',
			dataKey: 'minLength',
		},
		{
			name: 'masterdata.cutting.dimensions.length.maximum',
			dataKey: 'maxLength',
		},
		{
			name: 'masterdata.cutting.cutting-options.label',
			dataKey: 'cuttingMethodTranslation',
		},
		{ name: 'masterdata.cutting.cutting-options.kerf', dataKey: 'kerf' },
		{
			name: 'masterdata.cutting.cutting-options.clearance',
			dataKey: 'clearance',
		},
		{ name: 'masterdata.cutting.cutting-options.rear', dataKey: 'rear' },
		{
			name: 'masterdata.cutting.sorting.label',
			dataKey: 'sortingStrategyTranslation',
		},
		{ name: 'masterdata.cutting.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.cutting.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.cutting.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.cutting.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.cutting.newbtn.setups',
			click: () => this.openCreateUpdateSetup(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.cutting.setupImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['cutting:SetupPost', 'cutting:SetupPut', 'cutting:SetupList'],
		},
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.cutting.setupImportExport.export.button',
			click: () => this.onExport(),
			permissions: () => ['cutting:SetupList'],
		},
		{
			icon: () => 'acies_template',
			text: () => 'masterdata.cutting.setupImportExport.import.template.button',
			click: () => this.onDownloadTemplate(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private translator: TranslateService,
		private cuttingService: CuttingService,
		private notificationService: NotificationService,
		private sessionStorageService: SessionStorageService,
		private setupTransferService: SetupTransferService,
		private importValidatorService: ImportValidatorService,
	) {}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.isLoading = true;
		this.loadData();

		this.listenForLanguageChangeAndLoadData();
	}

	private listenForLanguageChangeAndLoadData(): void {
		this.sessionStorageService.watch('User.PreferredLanguage').subscribe(() => {
			if (this.tableSource) {
				this.loadData();
			}
		});
	}

	loadData(): void {
		this.cuttingService
			.getSetups()
			.pipe(
				takeUntil(this.unsubscribe),
				tap((data) => {
					this.isLoading = true;
					this.countSubject$.next(data.items.length);
				}),
				map((data) => {
					const setup$ = data.items.map((setup) => {
						return {
							self: setup.self,
							name: setup.name,
							description: setup.description,
							updatedDate: aciesDateFormat(new Date(setup.meta.updatedAt), true, false),
							updatedAtEpoch: setup.meta.updatedAt,
							updatedBy: this.cuttingService.getLastSegment(setup.meta.updatedBy.self),
							minLength: setup.dimensions.length.minimum,
							maxLength: setup.dimensions.length.maximum,
							cuttingMethod: this.cuttingService.getLastSegment(setup.cutting, ['method', 'self']),
							cuttingMethodTranslation: this.cuttingService.getTranslation(setup.cutting, ['method', 'self'], 'masterdata.cutting.cutting-options.method'),
							kerf: setup.cutting.kerf,
							clearance: setup.cutting.clearance,
							rear: setup.cutting.rear,
							angle: setup.cutting.angle,
							oversizing: setup.cutting.oversizing,
							sortingStrategy: this.cuttingService.getLastSegment(setup.sorting, ['strategy', 'self']),
							sortingStrategyTranslation: this.cuttingService.getTranslation(setup.sorting, ['strategy', 'self'], 'masterdata.cutting.sorting.strategy'),
						};
					});
					return setup$;
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((setups) => {
				this.tableSource.data = setups;
			});
	}

	handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
	}

	public onDownloadTemplate(): void {
		this.setupTransferService.downloadTemplate();
		this.notificationService.notify(['masterdata.cutting.setupImportExport.import.template.notification.header'], ['masterdata.cutting.setupImportExport.import.template.notification.content'], 'success');
	}

	public onExport(): void {
		this.setupTransferService.exportData(this.tableSource.data).subscribe({
			next: () => {
				this.notificationService.notify(['masterdata.cutting.setupImportExport.export.notification.success.header'], ['masterdata.cutting.setupImportExport.export.notification.success.content'], 'success');
			},
			error: (error: Error) => {
				console.error('Failed to export setups', error);
				this.notificationService.notify(['masterdata.cutting.setupImportExport.export.notification.error.header'], ['masterdata.cutting.setupImportExport.export.notification.error.content'], 'error');
				this.dialog.open(TransferErrorPopupComponent, {
					width: '450px',
					data: { message: error.message },
				});
			},
		});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.cutting.setupImportExport.import.title',
				subHeadingKey: 'masterdata.cutting.setupImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.cutting.setupImportExport.import.button',
				notificationHeader: 'masterdata.cutting.setupImportExport.import.notification.success.header',
				notificationContent: 'masterdata.cutting.setupImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.cutting.setupImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.cutting.setupImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.cutting.setupImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.cutting.setupImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.importValidatorService,
				data: this.setupTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateSetup(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteSetup(data['data']);
				break;
			default:
				break;
		}
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	openCreateUpdateSetup(setupData: object = null) {
		const dialogRef = this.dialog.open(CreateUpdateSetupDialogComponent, {
			disableClose: true,
			width: '650px',
			data: setupData,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	onDeleteSetup(setup: IResponseSetup) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.setups.heading'),
				content: this.translator.instant('masterdata.cutting.delete.setups.content', {
					name: setup.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.cuttingService
						.delete(setup.self)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.setups.notification.header'], ['masterdata.cutting.delete.setups.notification.content', { name: setup.name }], 'success');
							this.loadData();
						});
				}
			});
	}
}
